import { usePostProducts } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { PaymentsModal } from './PaymentsModal';

type FormData = {
  code: string;
  description: string;
};

type CreateProductModalProps = {
  onClose: () => void;
  onCreated: (productCode: string) => void;
};

export const CreateProductModal = ({
  onClose,
  onCreated,
}: CreateProductModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postProductMutation = usePostProducts();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { code: '', description: '' },
  });

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postProductMutation.mutateAsync({
          data: {
            code: formData.code.trim(),
            ...(formData.description.trim() && {
              description: formData.description.trim(),
            }),
          },
        });
        await queryClient.invalidateQueries({ queryKey: ['/products'] });
        showToast(
          <Toast type="success" message="Product created successfully" />
        );
        onCreated(formData.code.trim());
        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create product. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to create product: ${errorMessage}`}
          />
        );
      }
    },
    [postProductMutation, queryClient, showToast, onCreated, onClose]
  );

  const canSubmit = isValid && !postProductMutation.isPending;

  return (
    <PaymentsModal
      data-testid="create-product-modal"
      onClose={onClose}
      width="sm"
      onSubmit={handleSubmit(onSubmit)}
      header={<div className="text-main">Create Product</div>}
      body={
        <div className="flex flex-col space-y-f2">
          <ModalField
            hasError={!!errors.code}
            label="Code"
            labelWidth="w-f12"
          >
            <ModalTextInput
              data-testid="product-code-input"
              placeholder="Product code"
              {...register('code', {
                required: 'Product code is required',
                validate: (value) =>
                  value.trim() !== '' || 'Product code cannot be empty',
              })}
            />
          </ModalField>
          <ModalField label="Description" labelWidth="w-f12">
            <ModalTextInput
              data-testid="product-description-input"
              placeholder="Description (optional)"
              {...register('description')}
            />
          </ModalField>
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel={
              postProductMutation.isPending ? 'Creating...' : 'Create'
            }
            onCancel={onClose}
            onSubmit={handleSubmit(onSubmit)}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
